import {
	Component,
	Input,
	OnChanges,
	OnDestroy,
	SimpleChanges,
	ViewEncapsulation
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { FormioLoader, BaseFormIoCustomComponent, ResourceNameService } from 'dp-formio';
import { finalize, map, mergeMap, Observable, startWith, take, takeUntil } from 'rxjs';
import { SortHelper } from '../../helpers/sort.helper';
import { AssessmentControlComponent } from './control/assessment-control.component';
import {
	CategoryConfig,
	EvaluationItemsConfig,
	EvaluationLevelsConfig,
	OptionalInputConfig,
	ProcessConfig,
	ScoreConditionsConfig
} from './models/config.model';
var count = 0;
declare var $: any;
@Component({
	selector: 'app-assessment-wrapper',
	templateUrl: './assessment-wrapper.component.html',
	// styleUrls: ['./assessment-wrapper.component.scss']
	encapsulation: ViewEncapsulation.None,
	providers: [FormioLoader],
})
export class AssessmentWrapperComponent
	extends BaseFormIoCustomComponent<any>
	implements OnDestroy, OnChanges {
	dir: 'rtl' | 'ltr';
	@Input() required: boolean;

	@Input()
	viewOnly: boolean;

	@Input()
	data: any;

	@Input()
	customOptions: any;

	@Input()
	formioComponent: any;

	proccessResource = '';
	proccessSelect = '';
	processConfig: ProcessConfig;

	categoryResource = '';
	categorySelect = '';
	categories: CategoryConfig[] = [];

	chartData: any;

	constructor(
		private translateService: TranslateService,
		private dialog: MatDialog,
		private resourceNameService: ResourceNameService,
		private formioLoader: FormioLoader
	) {
		super();
		(this.translateService.onLangChange as Observable<LangChangeEvent>)
			.pipe(
				takeUntil(this.destroy$),
				startWith({
					lang: this.translateService.currentLang,
				}),
				map((data) => (data.lang == 'ar' ? 'rtl' : 'ltr'))
			)
			.subscribe((dir) => {
				this.dir = dir;
			});
	}

	ngOnChanges(changes: SimpleChanges) {
		this.formioLoader.loading = false;
		if ((changes.viewOnly || changes.customOptions) && this.customOptions) {
			if (this.viewOnly) {
				this.required = false;
			}
		}
		if (changes.customOptions && this.customOptions?.proccessResource) {
			this._interpolateData();
			this._getAssessmentsResources();
		}
	}

	onResized(event) {
		$('#bars').height(event.newHeight - 20);
	}

	openCategory(category: CategoryConfig) {
		this.openDialog(category);
	}

	openSubCategory(data: any) {
		this.openDialog(data.parent, data.key);
	}

	private openDialog(category?: CategoryConfig, subCategoryKey?: string) {
		const dialog = this.dialog.open(AssessmentControlComponent, {
			data: {
				dir: this.dir,
				data: this.data,
				viewOnly: this.viewOnly,
				customOptions: this.customOptions,
				processConfig: this.processConfig,
				categories: this.categories,
				currentLevel: category,
				selected: [category],
				subCategoryKey,
			},
			height: '100vh',
			width: '100vw',
			panelClass: 'full-screen-modal',
			disableClose: true,
		});
		dialog
			.afterClosed()
			.pipe(take(1), takeUntil(this.destroy$))
			.subscribe((_) => {
				this.notifyValue();
			});
	}

	notifyValue() {
		const invalid = this.required && this.categories.some((q) => q?.hasError);
		this.value = invalid ? null : true;
		this.valueChange.emit(this.value);
	}

	private _interpolateData(): void {
		this.proccessResource = this.formioComponent.interpolate(this.customOptions.proccessResource, {
			data: this.data,
		});
		this.proccessSelect = this.formioComponent.interpolate(this.customOptions.proccessSelect, {
			data: this.data,
		});

		this.categoryResource = this.formioComponent.interpolate(this.customOptions.categoryResource, {
			data: this.data,
		});
		this.categorySelect = this.formioComponent.interpolate(this.customOptions.categorySelect, {
			data: this.data,
		});
	}

	private _getCategoryAssessment(result): Observable<any[]> {
		if (result?.length) {
			this.processConfig = { _id: result[0]._id, ...result[0].data };

			this.processConfig.scoreConditions = result[0].data.scoreConditions.map((o) => {
				const oInput = new ScoreConditionsConfig();
				SortHelper.copyProps(o, oInput);
				return oInput;
			});

			this.processConfig.evaluationItems =
				result[0].data.evaluationItems?.map((o) => {
					const oInput = new EvaluationItemsConfig();
					SortHelper.copyProps(o, oInput);
					return oInput;
				}) || [];

			this.processConfig.evaluationLevels =
				result[0].data.evaluationLevels?.map((o) => {
					const oInput = new EvaluationLevelsConfig();
					SortHelper.copyProps(o, oInput);
					return oInput;
				}) || [];

			return this.resourceNameService.loadCustomByFilterdKey(
				this.categoryResource,
				`data.key__in=${this.processConfig.categoryAssessment.join()}${this.categorySelect}`
			);
		}
		return null;
	}

	private _getAssessmentsResources() {
		if (!this.formioComponent.visible) return;
		this.formioLoader.loading = true;
		this.resourceNameService
			.loadCustomByFilterdKey(this.proccessResource, this.proccessSelect)
			.pipe(
				takeUntil(this.destroy$),
				mergeMap((result) => this._getCategoryAssessment(result)),
				finalize(() => {
					this.formioLoader.loading = false;
				})
			)
			.subscribe((result: any[]) => {
				this.categories = SortHelper.recursiveSort(
					result.map<CategoryConfig>((c) => this._prepareCategoryConfig(c)),
					'asc'
				);

				this.chartData = {
					datasets: [
						{
							data: this.categories.map((q) => q.percentage),
							backgroundColor: this.categories.map((q) => q.color),
						},
					],
					labels: this.categories.map((q) => q.name),
				};

				this.categories.forEach((item) => {
					$(`#bar_${item._id} .p-progressbar-value`).css('background-color', item.color);
				});
				this.notifyValue();
			});
	}

	private _prepareCategoryConfig(c: any): CategoryConfig {
		{
			const item = new CategoryConfig();
			SortHelper.copyProps({ _id: c._id, ...c.data }, item);
			item.optionalInputs = !item.optionalInputs
				? []
				: item.optionalInputs.map((o) => {
					const oInput = new OptionalInputConfig();
					SortHelper.copyProps(o, oInput);
					return oInput;
				});
			item.processConfigObject = this.processConfig;
			return item;
		}
	}
}
